import { IMergeRequestItemDto } from "@kuika/kuika-cl-model/src/dtos/branch.dto";
import React from "react";
import BranchingService from "../../../services/branching-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import BranchingMergeRequestDetail, { type MergeRequestAction } from "./branching-merge-request-detail";
import "./branching-merge-requests.scss";

const REJECT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Rejecting", success: "Rejected" };
const CANCEL_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Cancelling", success: "Cancelled" };

function initialsOf(name?: string): string {
  const parts: string[] = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatRelativeTime(iso?: string): string {
  if (!iso) return "";
  const then: number = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const minutes: number = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours: number = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days: number = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-US");
}

interface IBranchingMergeRequestsProps {
  /** Reload the project after an approval applied changes to the target branch. */
  onAfterApprove?: () => void;
  /** Report the pending count so the hosting tab can show a badge. */
  onCountChange?: (pendingCount: number) => void;
}

interface IBranchingMergeRequestsState {
  isLoading: boolean;
  requests: IMergeRequestItemDto[];
  actionId: string | null;
  rejectingId: string | null;
  rejectComment: string;
  /** When set, the tab shows the read-only review detail for this request instead of the list. */
  openRequestId: string | null;
  /** Which action `actionId` is running, so its own button carries the status. */
  actionKind: MergeRequestAction | null;
  /** Where the reject/cancel stands. Owned by `SaveStatus`, never set directly. */
  actionStatus: KuikaButtonStatus;
}

class BranchingMergeRequests extends React.Component<IBranchingMergeRequestsProps, IBranchingMergeRequestsState> {
  state: IBranchingMergeRequestsState = {
    isLoading: false,
    requests: [],
    actionId: null,
    rejectingId: null,
    rejectComment: "",
    openRequestId: null,
    actionKind: null,
    actionStatus: "idle"
  };

  /**
   * Reject and cancel's state machine. The request moves to Decided once they land, and the button
   * that ran them goes with it — so its ✓ is held first, and only then does the list reload.
   */
  private readonly actionStatus: SaveStatus = new SaveStatus((actionStatus: KuikaButtonStatus): void => {
    this.setState({ actionStatus });
  });

  componentDidMount(): void {
    this.load();
  }

  componentWillUnmount(): void {
    this.actionStatus.dispose();
  }

  private load = async (): Promise<void> => {
    this.setState({ isLoading: true });
    try {
      const res = await BranchingService.GetMergeRequests(false);
      const requests: IMergeRequestItemDto[] = res?.data ?? [];
      this.setState({ requests, isLoading: false });
      const pendingCount: number = requests.filter((r) => r.status === "Pending").length;
      this.props.onCountChange?.(pendingCount);
    } catch {
      this.setState({ isLoading: false });
      KMainFunctions.displayErrorNotification("Failed to load merge requests.");
    }
  };

  private handleOpen = (request: IMergeRequestItemDto): void => {
    this.setState({ openRequestId: request.id });
  };

  private handleBack = (): void => {
    this.setState({ openRequestId: null });
  };

  // The detail view owns the approve/merge flow (it may need the conflict resolver). Once it lands,
  // it calls this back so the parent reloads the project + list and returns to the list.
  private handleApproved = (): void => {
    this.props.onAfterApprove?.();
    this.setState({ openRequestId: null });
    void this.load();
  };

  /** The list after a reject or cancel lands: the request leaves Open, and the detail closes. */
  private settleAction = (): void => {
    this.setState({ openRequestId: null, actionId: null, actionKind: null, rejectingId: null, rejectComment: "" });
    void this.load();
  };

  private handleReject = async (request: IMergeRequestItemDto, comment?: string): Promise<void> => {
    if (!this.actionStatus.canStart) return;
    const finalComment: string = (comment ?? this.state.rejectComment).trim();
    this.actionStatus.start();
    this.setState({ actionId: request.id, actionKind: "reject" });
    try {
      await BranchingService.RejectMergeRequest(request.id, finalComment);
      this.actionStatus.succeed(this.settleAction);
    } catch (error: unknown) {
      const err = error as { message?: string };
      this.actionStatus.fail();
      KMainFunctions.displayErrorNotification(err?.message || "Failed to reject merge request.");
      // The reject form stays open with its reason for another try.
      this.setState({ actionId: null, actionKind: null });
      await this.load();
    }
  };

  private handleCancel = async (request: IMergeRequestItemDto): Promise<void> => {
    if (!this.actionStatus.canStart) return;
    this.actionStatus.start();
    this.setState({ actionId: request.id, actionKind: "cancel" });
    try {
      await BranchingService.CancelMergeRequest(request.id);
      this.actionStatus.succeed(this.settleAction);
    } catch (error: unknown) {
      const err = error as { message?: string };
      this.actionStatus.fail();
      KMainFunctions.displayErrorNotification(err?.message || "Failed to cancel merge request.");
      this.setState({ actionId: null, actionKind: null });
      await this.load();
    }
  };

  private renderRequest = (request: IMergeRequestItemDto): React.ReactNode => {
    const isActing: boolean = this.state.actionId === request.id;
    const isRejecting: boolean = this.state.rejectingId === request.id;
    const statusKind: string = request.status.toLowerCase();
    // The action this row is running carries the status on its own button.
    const rejectStatus: KuikaButtonStatus | undefined =
      isActing && this.state.actionKind === "reject" ? this.state.actionStatus : undefined;
    const cancelStatus: KuikaButtonStatus | undefined =
      isActing && this.state.actionKind === "cancel" ? this.state.actionStatus : undefined;

    return (
      <div
        key={request.id}
        className="branch-merge-requests__item branch-merge-requests__item--clickable"
        data-status={statusKind}
        role="button"
        tabIndex={0}
        onClick={() => this.handleOpen(request)}
      >
        <div className="branch-merge-requests__item-head">
          <span className="branch-merge-requests__route">
            <strong>{request.sourceBranchName}</strong>
            <KuikaSvg name="arrow-right" type="arrows" width={13} height={13} />
            <strong>{request.targetBranchName}</strong>
          </span>
          <div className="branch-merge-requests__badges">
            <span className="branch-merge-requests__status" data-status={statusKind}>
              {request.status}
            </span>
            {request.isStale && (
              <span
                className="branch-merge-requests__status"
                data-status="stale"
                title="The branches changed since this request was opened"
              >
                Out of date
              </span>
            )}
          </div>
        </div>

        {request.title && <div className="branch-merge-requests__title">{request.title}</div>}
        {request.description && <div className="branch-merge-requests__desc">{request.description}</div>}

        <div className="branch-merge-requests__meta">
          {request.requestedBy?.name && (
            <>
              <span className="branch-merge-requests__avatar" aria-hidden="true">
                {initialsOf(request.requestedBy.name)}
              </span>
              <span>
                <span className="branch-merge-requests__meta-name">{request.requestedBy.name}</span> opened
              </span>
            </>
          )}
          {formatRelativeTime(request.createdAt) && (
            <>
              <span className="branch-merge-requests__meta-sep">·</span>
              <span>{formatRelativeTime(request.createdAt)}</span>
            </>
          )}
          {request.status !== "Pending" && request.reviewedBy?.name && (
            <>
              <span className="branch-merge-requests__meta-sep">·</span>
              <span>
                {request.status} by <span className="branch-merge-requests__meta-name">{request.reviewedBy.name}</span>
              </span>
            </>
          )}
        </div>
        {request.reviewComment && (
          <div className="branch-merge-requests__comment">
            <KuikaSvg name="message" width={14} height={14} />
            <span>{request.reviewComment}</span>
          </div>
        )}

        {request.status === "Pending" && (
          <div className="branch-merge-requests__actions" onClick={(e) => e.stopPropagation()}>
            {/* Approve moved into the detail view — it may need the conflict resolver, so the owner
                reviews first (click the card). Reject stays inline (no review needed). */}
            {request.canReview && !isRejecting && (
              <KuikaButton
                type="secondary"
                color="neutral"
                size="sm"
                disabled={isActing}
                onClick={() => this.setState({ rejectingId: request.id, rejectComment: "" })}
              >
                Reject
              </KuikaButton>
            )}
            {request.canCancel && !isRejecting && (
              <KuikaButton
                type="tertiary"
                color="neutral"
                size="sm"
                status={cancelStatus}
                statusLabels={CANCEL_STATUS_LABELS}
                disabled={isActing && cancelStatus === undefined}
                onClick={() => this.handleCancel(request)}
              >
                Cancel request
              </KuikaButton>
            )}
          </div>
        )}

        {isRejecting && (
          <div className="branch-merge-requests__reject" onClick={(e) => e.stopPropagation()}>
            <KuikaInput
              type="textarea"
              placeholder="Reason for rejection (optional)"
              value={this.state.rejectComment}
              onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                this.setState({ rejectComment: e.target.value })
              }
            />
            <div className="branch-merge-requests__reject-actions">
              <KuikaButton
                type="secondary"
                color="neutral"
                size="sm"
                disabled={isActing}
                onClick={() => this.setState({ rejectingId: null, rejectComment: "" })}
              >
                Cancel
              </KuikaButton>
              <KuikaButton
                type="primary"
                color="danger"
                size="sm"
                status={rejectStatus}
                statusLabels={REJECT_STATUS_LABELS}
                disabled={isActing && rejectStatus === undefined}
                onClick={() => this.handleReject(request)}
              >
                Reject request
              </KuikaButton>
            </div>
          </div>
        )}
      </div>
    );
  };

  render(): React.ReactNode {
    const { isLoading, requests, openRequestId, actionId } = this.state;

    // Option B: a selected request takes over the tab with a read-only review detail view.
    const openRequest: IMergeRequestItemDto | undefined = openRequestId
      ? requests.find((r) => r.id === openRequestId)
      : undefined;
    if (openRequest) {
      return (
        <BranchingMergeRequestDetail
          request={openRequest}
          actionInFlight={actionId != null}
          action={actionId === openRequest.id ? this.state.actionKind : null}
          actionStatus={this.state.actionStatus}
          onBack={this.handleBack}
          onApproved={this.handleApproved}
          onReject={this.handleReject}
          onCancel={this.handleCancel}
        />
      );
    }

    const pending: IMergeRequestItemDto[] = requests.filter((r) => r.status === "Pending");
    const decided: IMergeRequestItemDto[] = requests.filter((r) => r.status !== "Pending");

    return (
      <div className="branch-merge-requests">
        <div className="branch-merge-requests__toolbar">
          <div className="branch-merge-requests__toolbar-left">
            <KuikaSvg name="git-merge" width={16} height={16} />
            <span className="branch-merge-requests__toolbar-title">Merge requests</span>
            {pending.length > 0 && <span className="branch-merge-requests__toolbar-count">{pending.length}</span>}
          </div>
          <KuikaButton type="secondary" size="sm" disabled={isLoading} onClick={this.load}>
            <span className="branch-merge-requests__btn-label">
              <KuikaSvg name="refresh" width={14} height={14} />
              Refresh
            </span>
          </KuikaButton>
        </div>

        {isLoading && requests.length === 0 && (
          <KuikaSkeletonRegion label="Loading merge requests" className="branch-merge-requests__skeleton">
            <KuikaSkeletonList rows={4} withIcon lines={2} />
          </KuikaSkeletonRegion>
        )}

        {!isLoading && requests.length === 0 && (
          <div className="branch-merge-requests__empty">
            <KuikaSvg name="git-merge" width={26} height={26} />
            <span className="branch-merge-requests__empty-title">No merge requests</span>
            <span className="branch-merge-requests__empty-hint">
              When someone opens a request to merge into a branch, it shows up here for review.
            </span>
          </div>
        )}

        {requests.length > 0 && (
          <>
            {pending.length > 0 && (
              <div className="branch-merge-requests__section">
                <div className="branch-merge-requests__section-label">
                  Open
                  <span className="branch-merge-requests__section-count">{pending.length}</span>
                </div>
                {pending.map(this.renderRequest)}
              </div>
            )}
            {decided.length > 0 && (
              <div className="branch-merge-requests__section">
                <div className="branch-merge-requests__section-label">
                  Decided
                  <span className="branch-merge-requests__section-count">{decided.length}</span>
                </div>
                {decided.map(this.renderRequest)}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
}

export default BranchingMergeRequests;
